import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import { api } from "../../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "şimdi";
  if (m < 60) return `${m} dk önce`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} sa önce`;
  const d = Math.floor(h / 24);
  return `${d} gün önce`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* sessiz — zil kritik değil */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 90000);
    return () => clearInterval(t);
  }, [load]);

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) load();
  };

  const clickItem = (n) => {
    setOpen(false);
    if (!n.read) {
      api.post(`/notifications/${n.id}/read`).catch(() => {});
      setItems((prev) =>
        prev.map((i) => (i.id === n.id ? { ...i, read: true } : i))
      );
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.url && n.url.startsWith("/")) navigate(n.url);
  };

  const markAll = () => {
    api.post("/notifications/read-all").catch(() => {});
    setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    setUnread(0);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell-btn"
          aria-label="Bildirimler"
          className="relative flex h-9 w-9 flex-none items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition-colors hover:border-zinc-300 hover:text-zinc-900"
        >
          <Bell className="h-4 w-4" strokeWidth={1.75} />
          {unread > 0 && (
            <span
              data-testid="notification-bell-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[22rem] max-w-[calc(100vw-1rem)] p-0"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2.5">
          <span className="text-sm font-semibold text-zinc-900">Bildirimler</span>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAll}
              data-testid="notifications-mark-all"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-600 hover:text-violet-800"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Tümünü okundu işaretle
            </button>
          )}
        </div>
        <div className="max-h-[24rem] overflow-y-auto">
          {items.length === 0 ? (
            <div
              className="flex flex-col items-center gap-2 px-4 py-10 text-center"
              data-testid="notifications-empty"
            >
              <Inbox className="h-6 w-6 text-zinc-300" />
              <p className="text-xs text-zinc-500">Henüz bildirim yok.</p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-50">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => clickItem(n)}
                    data-testid={`notification-item-${n.id}`}
                    className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-zinc-50 ${
                      n.read ? "" : "bg-violet-50/50"
                    }`}
                  >
                    <span
                      className={`mt-1.5 h-2 w-2 flex-none rounded-full ${
                        n.read ? "bg-transparent" : "bg-violet-500"
                      }`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-semibold text-zinc-900">
                        {n.title}
                      </span>
                      {n.body && (
                        <span className="mt-0.5 block text-[11px] leading-snug text-zinc-500 line-clamp-2">
                          {n.body}
                        </span>
                      )}
                      <span className="mt-0.5 block text-[10px] text-zinc-400">
                        {timeAgo(n.created_at)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
